// Header row of the Product Attributes dashboard: title, client-computed
// stats, and the "+ Type" trigger. The create-type modal itself lives in
// AttributeTypesRail (it owns all type-CRUD modals) — this button only flips
// the screen-level `isCreateOpen` flag passed down to it.
//
// Stats are derived entirely from the types query + the usage query — no
// extra fetch. "Bound" counts values with any category binding
// (`value.boundCategoryIds.length > 0`, per-value field the BE now returns
// directly on GET .../types — see types.ts). "In use" is kept alongside it:
// it's a genuine types+usage join and immediately useful for the
// delete-vs-deactivate call, and there's room for both in the header.

import { AttributeTypeAdmin, AttributeUsage, usageFor } from 'apis/product-attributes';
import { Box, Flex, HStack, Icon, IconButtonWithTooltip, Text } from 'design-system';
import { useMemo } from 'react';
import { FiPlus } from 'react-icons/fi';

interface Props {
  types: AttributeTypeAdmin[];
  usage?: AttributeUsage;
  onAddType: () => void;
}

interface Stat {
  label: string;
  value: number;
}

const useStats = (types: AttributeTypeAdmin[], usage?: AttributeUsage): Stat[] => {
  return useMemo(() => {
    let valueCount = 0;
    let activeValueCount = 0;
    let usedValueCount = 0;
    let boundValueCount = 0;

    for (const type of types) {
      for (const value of type.values) {
        valueCount += 1;
        if (value.isActive) activeValueCount += 1;
        if (usageFor(usage, type.key, value.value) > 0) usedValueCount += 1;
        if ((value.boundCategoryIds?.length ?? 0) > 0) boundValueCount += 1;
      }
    }

    return [
      { label: 'Types', value: types.length },
      { label: 'Values', value: valueCount },
      { label: 'Active', value: activeValueCount },
      { label: 'In use', value: usedValueCount },
      { label: 'Bound', value: boundValueCount },
    ];
  }, [types, usage]);
};

export const AttributeDictionaryHeader = ({ types, usage, onAddType }: Props) => {
  const stats = useStats(types, usage);

  return (
    <Flex align='center' justify='space-between' mb='4' wrap='wrap' gap='3'>
      <Box>
        <Text fontSize='xl' fontWeight='bold'>
          Product Attributes
        </Text>
        <HStack mt='1' spacing='4'>
          {stats.map((stat) => (
            <Text key={stat.label} fontSize='sm' color='gray.500'>
              {stat.label}{' '}
              <Text as='span' fontWeight='semibold' color='gray.700'>
                {stat.value}
              </Text>
            </Text>
          ))}
        </HStack>
      </Box>
      <IconButtonWithTooltip aria-label='Add Type' label='Add Type' Icon={<Icon as={FiPlus} />} onClick={onAddType} />
    </Flex>
  );
};
